// Étiquettes de casiers 79 x 20 mm : numéro en gros + QR code généré
// automatiquement (contenu = le numéro). Rendu direct à 203 dpi, noir et blanc.
import QRCode from "qrcode";
import { PRINT_DPI } from "./depotLabelPrint";

// Support : page de 78,7 x 20 mm (réglage du pilote) sur laquelle l'étiquette
// autocollante réelle ne fait que 28,5 x 20 mm, côté gauche. On dessine donc le
// contenu dans cette zone de 28,5 mm, décalable vers la droite (réglage utilisateur).
export const CASIER_W_MM = 78.7;
export const CASIER_H_MM = 20;
export const CASIER_LABEL_W_MM = 28.5;
export const mmToPx = (mm) => Math.round((mm / 25.4) * PRINT_DPI);
export const PX_W = mmToPx(CASIER_W_MM);   // 626
export const PX_H = mmToPx(CASIER_H_MM);   // 160
export const LABEL_PX_W = mmToPx(CASIER_LABEL_W_MM); // 228

export function getOffsetMm() {
  try { const v = parseFloat(localStorage.getItem("casier_offset_mm")); return Number.isFinite(v) ? v : 0; } catch { return 0; }
}
export function setOffsetMm(v) { try { localStorage.setItem("casier_offset_mm", String(v)); } catch { /* ignore */ } }


// ---- Outils communs aux étiquettes « petit format » (casiers, reprise) ----
// Nouvelle page blanche 78,7 x 20 mm ; (x0, LW, H) = zone de l'autocollant réel.
export function newSheet() {
  const c = document.createElement("canvas");
  c.width = PX_W; c.height = PX_H;
  const ctx = c.getContext("2d");
  ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, PX_W, PX_H);
  ctx.fillStyle = "#000"; ctx.textBaseline = "alphabetic";
  return { c, ctx, x0: mmToPx(Math.max(0, getOffsetMm())), LW: LABEL_PX_W, H: PX_H };
}
export function finishSheet(c) {
  const ctx = c.getContext("2d");
  const img = ctx.getImageData(0, 0, PX_W, PX_H), d = img.data;
  for (let i = 0; i < d.length; i += 4) { const v = d[i] < 140 ? 0 : 255; d[i] = d[i + 1] = d[i + 2] = v; d[i + 3] = 255; }
  ctx.putImageData(img, 0, 0);
  return c;
}
export function previewOf(full) {
  const x0 = mmToPx(Math.max(0, getOffsetMm()));
  const c = document.createElement("canvas");
  c.width = LABEL_PX_W; c.height = PX_H;
  c.getContext("2d").drawImage(full, x0, 0, LABEL_PX_W, PX_H, 0, 0, LABEL_PX_W, PX_H);
  return c;
}
export function drawQr(ctx, text, x, y, maxPx, minMod = 2, maxMod = 4) {
  const qr = QRCode.create(text, { errorCorrectionLevel: "M" });
  const n = qr.modules.size;
  const mod = Math.max(minMod, Math.min(maxMod, Math.floor(maxPx / n)));
  for (let r = 0; r < n; r++) for (let q = 0; q < n; q++) if (qr.modules.get(r, q)) ctx.fillRect(x + q * mod, y + r * mod, mod, mod);
  return mod * n;
}

// Envoie des pages 78,7 x 20 mm à l'imprimante « petites étiquettes » : automatique
// dans l'app (Windows/Android), sinon une fenêtre d'impression avec une page par étiquette.
export async function printSheets(canvases) {
  const urls = canvases.map((c) => c.toDataURL("image/png"));
  if (window.SogoDesktop?.printImage) {
    for (const u of urls) {
      const r = await window.SogoDesktop.printImage(u, CASIER_W_MM, CASIER_H_MM, "casier");
      if (!r?.ok) return r || { ok: false, error: "Échec de l'impression" };
    }
    return { ok: true };
  }
  const win = window.open("", "_blank");
  if (!win) return { ok: false, error: "Fenêtre d'impression bloquée par le navigateur" };
  win.document.write(`<html><head><title>Étiquettes</title><style>
    @page { size: ${CASIER_W_MM}mm ${CASIER_H_MM}mm; margin: 0; }
    html, body { margin: 0; padding: 0; }
    img { width: ${CASIER_W_MM}mm; height: ${CASIER_H_MM}mm; display: block; page-break-after: always; }
  </style></head><body>${urls.map((u) => `<img src="${u}"/>`).join("")}<script>
    Promise.all([...document.images].map(i => i.decode())).then(() => window.print());
  </script></body></html>`);
  win.document.close();
  return { ok: true };
}

// « 1 2 5-8, 12 » -> ["1","2","5","6","7","8","12"] (chiffres uniquement, 12 max par numéro, 500 étiquettes max).
export function parseNumeros(text) {
  const out = [];
  for (const tok of String(text || "").split(/[\s,;]+/).filter(Boolean)) {
    const m = tok.match(/^(\d{1,12})\s*[-–]\s*(\d{1,12})$/);
    if (m) {
      const a = Number(m[1]), b = Number(m[2]);
      const w = m[1].startsWith("0") ? m[1].length : 0; // conserve les zéros de tête (001-010)
      if (b >= a && b - a < 500) for (let n = a; n <= b; n++) out.push(w ? String(n).padStart(w, "0") : String(n));
    } else if (/^\d{1,12}$/.test(tok)) out.push(tok);
  }
  return out.slice(0, 500);
}

// Page complète 78,7 x 20 mm à 203 dpi : numéro en gros, QR dessous, dans la zone de l'étiquette.
export function renderCasierLabel(numero) {
  const c = document.createElement("canvas");
  c.width = PX_W; c.height = PX_H;
  const ctx = c.getContext("2d");
  ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, PX_W, PX_H);
  ctx.fillStyle = "#000";
  const x0 = mmToPx(Math.max(0, getOffsetMm()));
  const marge = 8, gap = 8;

  // Numéro : la plus grande police grasse qui tient en largeur (hauteur de chiffre <= 70 px).
  let size = 98;
  const font = (s) => `bold ${s}px Arial, Helvetica, sans-serif`;
  ctx.font = font(size);
  const wText = ctx.measureText(numero).width;
  const avail = LABEL_PX_W - 2 * marge;
  if (wText > avail) size = Math.floor(size * avail / wText);
  ctx.font = font(size);
  const m = ctx.measureText(numero);
  const capH = Math.round(m.actualBoundingBoxAscent);
  ctx.textAlign = "center"; ctx.textBaseline = "alphabetic";

  // QR : modules entiers, le plus grand possible dans la hauteur restante.
  const qr = QRCode.create(numero, { errorCorrectionLevel: "M" });
  const n = qr.modules.size;
  const mod = Math.max(2, Math.min(4, Math.floor((PX_H - 2 * marge - capH - gap) / n)));
  const qrSize = mod * n;
  const bloc = capH + gap + qrSize;
  const top = Math.round((PX_H - bloc) / 2);
  ctx.fillText(numero, x0 + LABEL_PX_W / 2, top + capH);
  const qx = x0 + Math.round((LABEL_PX_W - qrSize) / 2), qy = top + capH + gap;
  for (let y = 0; y < n; y++) for (let x = 0; x < n; x++) if (qr.modules.get(y, x)) ctx.fillRect(qx + x * mod, qy + y * mod, mod, mod);

  const img = ctx.getImageData(0, 0, PX_W, PX_H), d = img.data;
  for (let i = 0; i < d.length; i += 4) { const v = d[i] < 140 ? 0 : 255; d[i] = d[i + 1] = d[i + 2] = v; d[i + 3] = 255; }
  ctx.putImageData(img, 0, 0);
  return c;
}

// Aperçu : uniquement la zone de l'étiquette réelle (28,5 x 20 mm).
export function renderCasierPreview(numero) {
  const full = renderCasierLabel(numero);
  const x0 = mmToPx(Math.max(0, getOffsetMm()));
  const c = document.createElement("canvas");
  c.width = LABEL_PX_W; c.height = PX_H;
  c.getContext("2d").drawImage(full, x0, 0, LABEL_PX_W, PX_H, 0, 0, LABEL_PX_W, PX_H);
  return c;
}

export async function printCasierLabels(numeros) {
  return printSheets(numeros.map((n) => renderCasierLabel(n)));
}
